import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { types } from 'mobx-state-tree';
import { api } from 'src/api';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { createAsync } from 'src/utils/asyncModel';

/** The service caps the page size at 100; the table pages and sorts what it is given. */
const DEFAULT_PAGE_SIZE = 100;

/** Statuses the pipeline still moves on its own, so the list is worth refreshing while any are present. */
const IN_PROGRESS_STATUSES = ['Pending', 'Processing', 'Extracted'];

/**
 * The one AP push state something else is still working on. A push is queued here and finished by
 * AppFolio through Kafka, so the row settles on a later read rather than on the call that started it.
 * `failed` and `needsReconciliation` are settled: they wait on a person, not on the pipeline.
 */
const UNSETTLED_AP_PUSH_STATUSES = ['pending'];
const getCurrentCompanyId = () => localStorage.getItem(USER_SETTINGS_KEYS.company) || '';
const getCompanyBillsAsync = createAsync(body => {
  return async function getCompanyBills(options) {
    var _body$page, _body$pageSize;
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;
    const resp = await api.BillAutomationBills.getCompanyBills({
      companyId,
      page: (_body$page = body === null || body === void 0 ? void 0 : body.page) !== null && _body$page !== void 0 ? _body$page : 1,
      pageSize: (_body$pageSize = body === null || body === void 0 ? void 0 : body.pageSize) !== null && _body$pageSize !== void 0 ? _body$pageSize : DEFAULT_PAGE_SIZE
    }, options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful && resp.response) this.setCompanyBills(resp.response);
    return resp;
  };
});
const uploadBillsBatchAsync = createAsync(body => {
  return async function uploadBillsBatch(options) {
    const companyId = getCurrentCompanyId();
    return api.BillAutomationBills.uploadBillsBatch(_objectSpread({
      companyId
    }, body), options);
  };
});
const getBillAllocationsAsync = createAsync(body => {
  return async function getBillAllocations(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;
    return api.BillAutomationBills.getBillAllocations({
      companyId,
      billId: body.billId
    }, options);
  };
});

/**
 * Creates the allocation bill for each service named and approves whatever is ready. Called again
 * with no services to follow a table from created to approved, which the service does a step at a
 * time because the allocation itself is worked out between calls.
 */
const allocateBillAsync = createAsync(body => {
  return async function allocateBill(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;
    return api.BillAutomationBills.allocateBill({
      companyId,
      billId: body.billId,
      services: body.services
    }, options);
  };
});

/**
 * Deletes the bill, and drops it from the list when the service says it is gone. A bill held by its
 * allocations comes back refused with the reason, and stays where it is.
 */
/**
 * Approve-on-attach (LB-5766). Bill-scoped route, so no company id — the service settles ownership
 * against the bill's own company. The refreshed list picks up the new status (Processing/Extracted
 * are in-progress, so the list poller re-arms on its own).
 */
const attachBillAsync = createAsync(body => {
  return async function attachBill(options) {
    return api.BillAutomationBills.attachBill(body, options);
  };
});
const deleteBillAsync = createAsync(body => {
  return async function deleteBill(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;
    const resp = await api.BillAutomationBills.deleteBill({
      companyId,
      billId: body.billId
    }, options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful) this.removeBill(body.billId);
    return resp;
  };
});

/** Company-wide bill list behind the Bills page table. */
const CompanyBillsStore = types.model('CompanyBillsStore', {
  items: types.optional(types.frozen(), []),
  total: types.optional(types.number, 0),
  page: types.optional(types.number, 1),
  pageSize: types.optional(types.number, DEFAULT_PAGE_SIZE),
  allocateBillAsync,
  attachBillAsync,
  deleteBillAsync,
  getBillAllocationsAsync,
  getCompanyBillsAsync,
  uploadBillsBatchAsync
}).actions(store => ({
  setCompanyBills(page) {
    var _page$items, _page$total, _page$page, _page$pageSize;
    store.items = (_page$items = page.items) !== null && _page$items !== void 0 ? _page$items : [];
    store.total = (_page$total = page.total) !== null && _page$total !== void 0 ? _page$total : 0;
    store.page = (_page$page = page.page) !== null && _page$page !== void 0 ? _page$page : 1;
    store.pageSize = (_page$pageSize = page.pageSize) !== null && _page$pageSize !== void 0 ? _page$pageSize : DEFAULT_PAGE_SIZE;
  },
  /** Takes a deleted bill out of the list without waiting for the next read of it. */
  removeBill(billId) {
    const remaining = store.items.filter(item => item.billId !== billId);
    if (remaining.length === store.items.length) return;
    store.items = remaining;
    store.total = Math.max(store.total - 1, 0);
  }
})).views(store => ({
  get hasBillsInProgress() {
    return store.items.some(item => IN_PROGRESS_STATUSES.includes(item.extractStatus));
  },
  /** Whether any bill's push is still on its way, which is what keeps the list refreshing. */
  get hasUnsettledApPush() {
    return store.items.some(item => {
      var _item$apPush;
      return UNSETTLED_AP_PUSH_STATUSES.includes((_item$apPush = item.apPush) === null || _item$apPush === void 0 ? void 0 : _item$apPush.status);
    });
  },
  getBill(billId) {
    return store.items.find(item => item.billId === billId);
  }
}));
export const companyBills = types.optional(CompanyBillsStore, {});